import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@vd/ui';
import { isLiveTicketEvent, type LiveEvent, type NarrationAssignmentNote } from '@vd/types';
import { api } from '../../lib/api';
import styles from './NarrationAssignmentNotes.module.css';

/** The query key for a ticket's narration notes, for anything that changes who is assigned. */
export const narrationAssignmentsKey = (ticketId: string) => ['narration-assignments', ticketId] as const;

type NoteAction =
  | { kind: 'assign'; noteId: string; userId: string }
  | { kind: 'dismiss'; noteId: string };

/**
 * What a walkthrough's narration said about who takes this ticket (TOADO-474).
 *
 * Unresolved names are shown next to the words they came from, for a person to
 * assign with one click or dismiss. Clear automatic matches are already resolved.
 * A name it
 * could not place is shown the same way, with the people it could mean.
 */
function describe(note: NarrationAssignmentNote): string {
  const name = note.person?.name ?? 'Someone';
  if (note.outcome === 'suggested') {
    if (note.reason === 'named') {
      return note.isSelf
        ? 'The person recording said they would take this.'
        : `The walkthrough asked for ${note.candidates[0]?.name ?? note.heardName} to take this.`;
    }
    if (note.reason === 'ambiguous') {
      return `The walkthrough named “${note.heardName}”, and more than one person on this project has that name.`;
    }
    if (note.reason === 'close_spelling') {
      return `The walkthrough named “${note.heardName}”. Nobody on this project has that name, but it is close to someone who does.`;
    }
    if (note.reason === 'hedged') {
      return note.isSelf
        ? 'The person recording may have offered to take this, but not as a plain yes, so nobody was assigned.'
        : `The walkthrough named “${note.heardName}” in a sentence that may not be asking them to take this, so nobody was assigned.`;
    }
    return `The walkthrough named “${note.heardName}”, and nobody on this project has that name.`;
  }
  const who = note.isSelf ? 'The person recording said they would take this' : `${name} was named in the walkthrough`;
  switch (note.reason) {
    case 'removed_by_person':
      return `${who} again, after they had been taken off this ticket.`;
    case 'no_project_access':
      return `${who}, but cannot see this project.`;
    default:
      return `${who}, but was not assigned.`;
  }
}

export function NarrationAssignmentNotes({ ticketId, projectId }: { ticketId: string; projectId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const notesQ = useQuery({
    queryKey: narrationAssignmentsKey(ticketId),
    queryFn: () => api.listNarrationAssignments(ticketId),
  });

  /**
   * Listened for here, not by each page that shows the notes.
   *
   * The assignment pass runs after the commit, while somebody may already be
   * reading, and announces its notes with ticket.assigned. So does anyone changing
   * assignees by hand, which can retire a note. One subscription where the notes
   * are shown means no page can forget it.
   */
  useEffect(() => api.subscribe((event: LiveEvent) => {
    if (event.type === 'ticket.assigned' && isLiveTicketEvent(event, ticketId)) {
      void qc.invalidateQueries({ queryKey: narrationAssignmentsKey(ticketId) });
    }
  }), [qc, ticketId]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: narrationAssignmentsKey(ticketId) });
    void qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    void qc.invalidateQueries({ queryKey: ['tickets', projectId] });
  };

  const act = useMutation({
    mutationFn: async (action: NoteAction): Promise<void> => {
      if (action.kind === 'assign') await api.assignFromNarration(ticketId, action.noteId, action.userId);
      else await api.dismissNarrationAssignment(ticketId, action.noteId);
    },
    onSuccess: refresh,
    onError: (error: unknown) => {
      toast.push({
        variant: 'danger',
        title: 'That did not save',
        description: error instanceof Error ? error.message : undefined,
      });
      // Somebody else may have changed it first; show what is there now.
      refresh();
    },
  });

  const notes = notesQ.data?.notes ?? [];
  if (notes.length === 0) return null;
  const canChange = notesQ.data?.canChange ?? false;

  return (
    <ul className={styles.notes} aria-label="Assignees from the walkthrough">
      {notes.map((note) => (
        <li key={note.id} className={styles.note} data-outcome={note.outcome}>
          <p className={styles.what}>{describe(note)}</p>
          {note.sentence && <q className={styles.said}>{note.sentence}</q>}
          {canChange && (
            <div className={styles.actions}>
              {note.candidates.map((candidate) => (
                <Button
                  type="button"
                  key={candidate.id}
                  size="sm"
                  variant="secondary"
                  disabled={act.isPending}
                  onClick={() => act.mutate({ kind: 'assign', noteId: note.id, userId: candidate.id })}
                >
                  Assign {candidate.name}
                </Button>
              ))}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={act.isPending}
                onClick={() => act.mutate({ kind: 'dismiss', noteId: note.id })}
              >
                Dismiss
              </Button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
